import React, { useState, useEffect, useRef } from 'react';
import { Search, Bell, Settings, User } from 'lucide-react';

interface HeaderProps {
  onTimeRangeChange?: (timeRange: string) => void;
  onNavigate?: (page: string) => void;
}

const Header: React.FC<HeaderProps> = ({ onTimeRangeChange, onNavigate }) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('Last 1h');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  
  // Search suggestions that match metrics and charts
  const suggestions = [
    // Metrics page items
    { title: "Key metrics", page: "metrics" },
    { title: "Response Time Latency", page: "metrics" },
    { title: "Device Distribution", page: "metrics" },
    { title: "Calls over time", page: "metrics" },
    { title: "Average Response Time", page: "metrics" },
    // Overview page items
    { title: "Recently viewed dashboards", page: "overview" },
    { title: "Latest from the blog", page: "overview" },
    // Database page items
    { title: "Database Logs", page: "database" },
    { title: "Input traffic by day & region", page: "database" },
    // Alerts page items
    { title: "Alerts", page: "alerts" },
    { title: "Total Alerts", page: "alerts" },
    { title: "Total Critical", page: "alerts" },
    { title: "Total Warning", page: "alerts" },
    { title: "Service Down", page: "alerts" },
    { title: "Instance Down", page: "alerts" },
    // Settings page items
    { title: "Settings", page: "settings" },
    { title: "Account Settings", page: "settings" },
    { title: "Notification Settings", page: "settings" },
    { title: "Display Settings", page: "settings" },
    { title: "Analytics Settings", page: "settings" }
  ];
  
  // Filter suggestions based on search query
  const filteredSuggestions = suggestions.filter(suggestion => {
    if (!searchQuery) return false;
    
    const query = searchQuery.toLowerCase();
    const title = suggestion.title.toLowerCase();
    
    // Direct match
    if (title.includes(query)) return true;
    
    // Match individual words in multi-word titles
    const titleWords = title.split(' ');
    return titleWords.some(word => word.startsWith(query));
  });
  
  // Handle clicks outside of the search container
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // This makes the selected value available in the window object for debugging
  useEffect(() => {
    // @ts-ignore
    window.__DEBUG_TIME_RANGE__ = selectedTimeRange;
  }, [selectedTimeRange]);
  
  const handleTimeRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTimeRange = e.target.value;
    setSelectedTimeRange(newTimeRange);
    
    // If a callback was provided, pass the selected value up to the parent
    if (onTimeRangeChange) {
      onTimeRangeChange(newTimeRange);
    }
    
    // Log the selected value to the console for debugging
    console.log('Selected time range:', newTimeRange);
  };
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSuggestions(true);
    
    // Direct navigation for exact alert-related terms
    const alertTerms = ["alerts", "total alerts", "total critical", "total warning", "service down", "instance down"];
    if (alertTerms.includes(value.toLowerCase()) && onNavigate) {
      onNavigate("alerts");
      setSearchQuery('');
      setShowSuggestions(false);
    }
    
    // Direct navigation for settings-related terms
    const settingsTerms = ["settings", "account settings", "notification settings", "display settings", "analytics settings"];
    if (settingsTerms.includes(value.toLowerCase()) && onNavigate) {
      onNavigate("settings");
      setSearchQuery('');
      setShowSuggestions(false);
    }
  };
  
  const handleSuggestionClick = (suggestion: { title: string, page: string }) => {
    console.log(`Navigating to ${suggestion.page} for: ${suggestion.title}`);
    setSearchQuery('');
    setShowSuggestions(false);
    
    // If a navigation callback was provided, use it to navigate to the page
    if (onNavigate) {
      onNavigate(suggestion.page);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim() !== '') {
      // Try to find the first matching suggestion
      if (filteredSuggestions.length > 0 && onNavigate) {
        const firstSuggestion = filteredSuggestions[0];
        onNavigate(firstSuggestion.page);
        setSearchQuery('');
        setShowSuggestions(false);
      }
    }
  };
  
  return (
    <header className="dashboard-header">
      <div className="header-left">
        <img src="/logo.png" alt="Logo" className="dashboard-logo" />
        <h1 className="dashboard-title">Tag Onsite - Nice 2025</h1>
      </div>
      
      <div className="header-center">
        <div className="search-container" ref={searchContainerRef}>
          <Search size={16} />
          <input 
            type="text" 
            placeholder="Search dashboards, metrics..." 
            className="search-input"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(true)}
          />
          {showSuggestions && searchQuery && (
            <div className="search-suggestions">
              {filteredSuggestions.length > 0 ? (
                <>
                  {/* Group suggestions by page type */}
                  {filteredSuggestions.some(s => s.page === "metrics") && (
                    <div className="search-suggestion-category">
                      <div className="search-suggestion-category-title">Metrics</div>
                      {filteredSuggestions
                        .filter(s => s.page === "metrics")
                        .map((suggestion, index) => (
                          <div 
                            key={`metrics-${index}`} 
                            className="search-suggestion-item"
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            {suggestion.title}
                          </div>
                        ))
                      }
                    </div>
                  )}
                  
                  {filteredSuggestions.some(s => s.page === "overview") && (
                    <div className="search-suggestion-category">
                      <div className="search-suggestion-category-title">Overview</div>
                      {filteredSuggestions
                        .filter(s => s.page === "overview")
                        .map((suggestion, index) => (
                          <div 
                            key={`overview-${index}`} 
                            className="search-suggestion-item"
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            {suggestion.title}
                          </div>
                        ))
                      }
                    </div>
                  )}
                  
                  {filteredSuggestions.some(s => s.page === "database") && (
                    <div className="search-suggestion-category">
                      <div className="search-suggestion-category-title">Database</div>
                      {filteredSuggestions
                        .filter(s => s.page === "database")
                        .map((suggestion, index) => (
                          <div 
                            key={`database-${index}`} 
                            className="search-suggestion-item"
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            {suggestion.title}
                          </div>
                        ))
                      }
                    </div>
                  )}
                  
                  {filteredSuggestions.some(s => s.page === "alerts") && (
                    <div className="search-suggestion-category">
                      <div className="search-suggestion-category-title">Alerts</div>
                      {filteredSuggestions
                        .filter(s => s.page === "alerts")
                        .map((suggestion, index) => (
                          <div 
                            key={`alerts-${index}`} 
                            className="search-suggestion-item"
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            {suggestion.title}
                          </div>
                        ))
                      }
                    </div>
                  )}
                  
                  {filteredSuggestions.some(s => s.page === "settings") && (
                    <div className="search-suggestion-category">
                      <div className="search-suggestion-category-title">Settings</div>
                      {filteredSuggestions
                        .filter(s => s.page === "settings")
                        .map((suggestion, index) => (
                          <div 
                            key={`settings-${index}`} 
                            className="search-suggestion-item"
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            {suggestion.title}
                          </div>
                        ))
                      }
                    </div>
                  )}
                </>
              ) : (
                <div className="search-no-results">
                  No matching results found
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="header-right">
        <button className="header-icon-btn">
          <Bell size={18} />
        </button>
        <button className="header-icon-btn">
          <Settings size={18} />
        </button>
        <button className="header-icon-btn">
          <User size={18} />
        </button>
        <div className="time-range-selector">
          <select 
            value={selectedTimeRange}
            onChange={handleTimeRangeChange}
            data-testid="time-range-selector"
            id="time-range-selector"
          >
            <option>Last 10m</option>
            <option>Last 30m</option>
            <option>Last 1h</option>
            <option>Last 6h</option>
            <option>Last 24h</option>
          </select>
        </div>
      </div>
    </header>
  );
};

export default Header;
